/**
 * Runrug — PDP / PLP interaction tracking
 *
 * This file has exactly one job: describe what the visitor did, and publish it
 * on Shopify's customer-event bus. It never names a destination. The custom
 * pixel decides where events go, so a destination can change without touching
 * the theme again.
 *
 * Why this exists at all: a Shopify custom pixel runs in a sandboxed iframe and
 * cannot see the storefront DOM. Without this file the pixel only ever receives
 * Shopify's own standard events — no colour clicks, no length entries, no
 * filter ticks.
 *
 * Load order: registered in snippets/importmap.liquid, loaded with the deferred
 * (non-critical) group in snippets/script-assets.liquid. It attaches two
 * delegated document listeners and a handful of ThemeBus subscriptions, and
 * does no work at all until someone interacts.
 *
 * Requires snippets/rr-analytics-context.liquid to have set window.__rrContext.
 */

import { ThemeBus } from "@theme/events-bus";
import { EVENTS, DEBOUNCE_MS } from "@theme/event-constants";

/* -------------------------------------------------------------------------- */
/* Core                                                                        */
/*                                                                             */
/* WHY THIS POSTS DIRECTLY                                                     */
/* Originally the theme only published on Shopify's customer-event bus and the */
/* custom pixel forwarded to the collector. That doesn't work: verified on the */
/* live preview, Shopify.analytics.publish() returns true and the event is     */
/* accepted, but a custom pixel never receives it — not via all_events, and    */
/* not via an explicit per-name subscribe either. Standard events like         */
/* checkout_completed come through fine; custom ones are silently dropped.     */
/*                                                                             */
/* So the theme posts to the collector itself. The pixel keeps handling        */
/* checkout_completed, and because both use the same _shopify_y value as       */
/* client_id, clicks and orders still join.                                    */
/*                                                                             */
/* publish() is still called, harmlessly — if Shopify fixes the relay, or      */
/* another pixel wants these events, they're already on the bus.               */
/* -------------------------------------------------------------------------- */

const COLLECTOR = "https://web-events-collector-production.up.railway.app/collect";

const ctx = () => window.__rrContext || {};

/** Seven context fields are columns; everything else goes in props. */
const CONTEXT_KEYS = [
  "surface", "market", "item_id", "item_handle",
  "item_name", "rr_product_type", "collection_handle",
];

/** Shopify's visitor id. Same value the pixel reports as event.clientId. */
function clientId() {
  const m = document.cookie.match(/(?:^|;\s*)_shopify_y=([^;]+)/);
  return m ? m[1] : null;
}

/* --- consent ------------------------------------------------------------- */
/* Nothing is sent until consent is known. Events that happen before then are */
/* held, then either flushed or dropped once the answer arrives.              */

let consentKnown = false;
let consentAllowed = false;
const pending = [];

function readConsent() {
  try {
    const cp = window.Shopify?.customerPrivacy;
    if (cp && typeof cp.analyticsProcessingAllowed === "function") {
      consentAllowed = cp.analyticsProcessingAllowed();
      consentKnown = true;
      return true;
    }
  } catch (_) {}
  return false;
}

function flush() {
  if (!consentKnown) return;
  const held = pending.splice(0, pending.length);
  if (!consentAllowed) return; // consent refused — drop, don't send
  held.forEach(post);
}

try {
  window.Shopify?.loadFeatures?.(
    [{ name: "consent-tracking-api", version: "0.1" }],
    () => { readConsent(); flush(); }
  );
} catch (_) {}
document.addEventListener("visitorConsentCollected", () => { readConsent(); flush(); });
if (readConsent()) flush();

/* --- transport ----------------------------------------------------------- */

function post(body) {
  try {
    fetch(COLLECTOR, {
      method: "POST",
      keepalive: true, // survives the page navigating away
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (_) {
    /* no-op by design — tracking must never break the storefront */
  }
}

/**
 * Record one event. Deliberately swallows errors.
 */
function emit(name, params = {}) {
  const all = { ...ctx(), ...params };

  // Still publish on the bus — costs nothing, and other pixels may want it.
  try { window.Shopify?.analytics?.publish(name, all); } catch (_) {}

  const body = {
    event_id: (crypto?.randomUUID?.() || String(Date.now()) + Math.random()),
    occurred_at: new Date().toISOString(),
    client_id: clientId(),
    event_name: name,
    page_url: location.href,
    referrer: document.referrer || undefined,
    props: {},
  };
  for (const k of CONTEXT_KEYS) {
    if (all[k] !== undefined && all[k] !== null && all[k] !== "") body[k] = all[k];
  }
  for (const k of Object.keys(all)) {
    if (!CONTEXT_KEYS.includes(k)) body.props[k] = all[k];
  }

  if (!body.client_id) return; // no visitor id yet — nothing to join on

  if (!consentKnown) {
    if (pending.length < 50) pending.push(body); // bounded, in case consent never resolves
    return;
  }
  if (consentAllowed) post(body);
}

/** Trim to GA4's 100-char parameter ceiling, in case we ever add that destination. */
const str = (v) => (v == null ? undefined : String(v).slice(0, 100));

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

/* -------------------------------------------------------------------------- */
/* Clicks                                                                      */
/*                                                                             */
/* One capture-phase listener. Capture rather than bubble so we still see the   */
/* click if something downstream calls stopPropagation. Every branch returns,   */
/* because these elements nest — a swatch sits inside a card sits inside a grid */
/* — and without the returns one click would fire three events.                */
/* -------------------------------------------------------------------------- */

document.addEventListener(
  "click",
  (e) => {
    const t = e.target;
    if (!t || typeof t.closest !== "function") return;

    /* --- PDP: category tab strip at the top of the product content --------- */
    /* The "collection filtration" — switches between the stair runner / hall  */
    /* runner / carpet / rug version of the same design.                      */
    const tab = t.closest("calculator-tab-header-group [data-tab-item]");
    if (tab) {
      return emit("pdp_category_tab_click", {
        tab_label: str(tab.dataset.tabItem),
        tab_product_id: str(tab.dataset.tabProductId),
        was_selected: tab.dataset.tabSelected === "true",
      });
    }

    /* --- PDP: inline colour range under the price ------------------------- */
    /* Needs the data-rr-colour-swatch patch in product-colour-swatch.liquid. */
    const swatch = t.closest("[data-rr-colour-swatch]");
    if (swatch) {
      return emit("pdp_colour_swatch_click", {
        colour_label: str(swatch.dataset.colourLabel),
        colour_product_id: str(swatch.dataset.colourProductId),
        position: num(swatch.dataset.position),
        is_current: swatch.hasAttribute("aria-current"),
      });
    }

    /* --- PDP: calculator step rows ---------------------------------------- */
    /* One selector covers all five steps; step_type says which.              */
    /* colour | measurements | tape_binding | underlay | stair_rods           */
    const step = t.closest("[data-step-click-event]");
    if (step) {
      const type = (step.dataset.stepClickEvent || "").replace("toggle-step-", "");
      return emit("pdp_step_open", {
        step_type: str(type),
        step_number: num(
          step.querySelector("[data-calculator-step-number]")?.textContent?.trim()
        ),
        already_saved: step.dataset.calculatorRowSaved === "true",
      });
    }

    /* --- PDP: colour chosen inside the calculator drawer ------------------- */
    /* Needs the data-colour-label patch in calculator-colour-selector-item.  */
    const colourItem = t.closest("calculator-colour-selector-item");
    if (colourItem) {
      return emit("pdp_colour_select", {
        colour_label: str(colourItem.dataset.colourLabel),
        colour_product_id: str(colourItem.dataset.colourProductId),
        was_selected: colourItem.dataset.selected === "true",
      });
    }

    /* --- PDP: tape / underlay / stair-rod option cards --------------------- */
    /* The card carries no step type, so derive the group from its wrapper.   */
    const card = t.closest("[data-calculator-card]");
    if (card) {
      const wrapper = card.closest(
        "underlay-selector, tape-selector, stair-rods-selector"
      );
      return emit("pdp_option_select", {
        option_group: str(wrapper ? wrapper.tagName.toLowerCase() : "unknown"),
        option_label: str(
          card.dataset.calculatorCardTitle || card.dataset.calculatorProductTitle
        ),
        option_variant_id: str(card.dataset.productId),
        was_selected: card.dataset.selected === "true",
      });
    }

    /* --- PDP: save selection in the calculator drawer footer --------------- */
    if (t.closest("[data-save-selection]")) {
      return emit("pdp_calculator_save", {
        steps_completed: document.querySelectorAll(
          '[data-step-click-event][data-calculator-row-saved="true"]'
        ).length,
      });
    }

    /* --- PDP: quantity + / - --------------------------------------------- */
    const qtyBtn = t.closest("quantity-selector-component [data-action]");
    if (qtyBtn) {
      const input = qtyBtn
        .closest("quantity-selector-component")
        ?.querySelector('input[name="quantity"], [ref="input"]');
      const from = num(input?.value);
      const dir = qtyBtn.dataset.action; // increase | decrease
      return emit("pdp_quantity_change", {
        direction: str(dir),
        quantity_from: from,
        quantity_to: from == null ? undefined : dir === "increase" ? from + 1 : from - 1,
      });
    }

    /* --- PDP: add to cart -------------------------------------------------- */
    /* Fires on the click. pdp_add_to_cart_success below fires only if the    */
    /* line actually lands, so the gap between them is your failure rate.     */
    const atc = t.closest("add-to-cart-component button[type='submit']");
    if (atc) {
      return emit("pdp_add_to_cart_click", {
        variant_id: str(
          document.querySelector('input[name="id"][ref="variantId"]')?.value
        ),
        quantity: num(document.querySelector('input[name="quantity"]')?.value) || 1,
        has_calculator_selection:
          document.querySelectorAll(
            '[data-step-click-event][data-calculator-row-saved="true"]'
          ).length > 0,
        is_disabled: atc.disabled === true,
      });
    }

    /* --- PLP: faceted-navigation filter links ----------------------------- */
    /* These navigate away, so the pixel must send with keepalive.            */
    const facet = t.closest("[data-faceted-filter-link]");
    if (facet) {
      return emit("plp_facet_click", {
        filter_group: str(facet.dataset.filterParam || ""),
        filter_value: str(facet.dataset.value),
        filter_action: facet.hasAttribute("data-faceted-active-filter-option")
          ? "remove"
          : "apply",
      });
    }

    /* --- PLP: clear filters ------------------------------------------------ */
    if (t.closest(".active-filter-clear-all")) {
      return emit("plp_filter_clear", { scope: "all" });
    }
    const activeChip = t.closest("[data-faceted-active-filter]");
    if (activeChip) {
      return emit("plp_filter_clear", {
        scope: "one",
        filter_group: str(activeChip.dataset.filterParam),
      });
    }

    /* --- PLP: filter group accordion (vertical + mobile layouts) ----------- */
    /* Scoped to the header/trigger only. The whole group is wrapped in       */
    /* [data-filter-accordion], so matching that alone fires on every         */
    /* checkbox click inside it too.                                          */
    const accTrigger = t.closest("[data-filter-accordion] [data-accordion-trigger]");
    if (accTrigger) {
      const group = accTrigger.closest("[data-filter-accordion]");
      return emit("plp_filter_accordion_open", {
        filter_group: str(group?.dataset.filterAccordion || accTrigger.textContent?.trim().slice(0, 60)),
      });
    }

    /* --- PLP: grid density switch ------------------------------------------ */
    const gridBtn = t.closest(
      "[data-filter-grid-switch] button[data-mobile-cols], [data-filter-grid-switch] button[data-desktop-cols]"
    );
    if (gridBtn) {
      return emit("plp_grid_switch", {
        grid_mode: str(
          gridBtn.closest("[data-filter-grid-switch]")?.dataset.filterGridSwitch
        ),
        grid_cols: num(gridBtn.dataset.mobileCols || gridBtn.dataset.desktopCols),
      });
    }

    /* --- PLP: load more ---------------------------------------------------- */
    if (t.closest("[data-load-more-button]")) {
      return emit("plp_load_more", {
        products_shown_before: num(
          document.querySelector("[data-shown-products-count]")?.textContent?.trim()
        ),
      });
    }

    /* --- PLP: colour swatch on a product card ------------------------------ */
    /* Scoped to [data-card-swatches] on purpose: [data-swatch-button] alone   */
    /* also matches the swatches inside the filter panel.                     */
    const cardSwatch = t.closest("[data-card-swatches] a.swatch-link");
    if (cardSwatch) {
      const parentCard = cardSwatch.closest("[data-product-card]");
      return emit("plp_card_swatch_click", {
        colour_label: str(cardSwatch.dataset.colourLabel || cardSwatch.title),
        card_item_handle: str(parentCard?.dataset.productHandle),
      });
    }

    /* --- PLP: product card click-through ----------------------------------- */
    /* Needs the data-product-id / -handle / -position patch in               */
    /* blocks/product-card.liquid. Last branch because cards contain          */
    /* swatches and sample buttons, both handled above.                       */
    const productCard = t.closest("[data-product-card]");
    if (productCard && t.closest("a")) {
      return emit("plp_product_click", {
        card_item_id: str(productCard.dataset.productId),
        card_item_handle: str(productCard.dataset.productHandle),
        position: num(productCard.dataset.position),
      });
    }
  },
  { capture: true }
);

/* -------------------------------------------------------------------------- */
/* Changes                                                                     */
/* -------------------------------------------------------------------------- */

let measureTimer;

document.addEventListener("change", (e) => {
  const t = e.target;
  if (!t || typeof t.closest !== "function") return;

  /* --- PDP: length / width entry ---------------------------------------- */
  /* Debounced: someone typing "450" can fire several change events, and we  */
  /* only want the value they settled on.                                    */
  const measure = t.closest("[data-calculator-measure-key]");
  if (measure) {
    clearTimeout(measureTimer);
    const key = measure.dataset.calculatorMeasureKey; // length | width | steps_calc | ...
    const value = measure.value;
    measureTimer = setTimeout(() => {
      emit("pdp_measure_input", {
        measure_key: str(key),
        measure_value: str(value),
        measure_unit: document.querySelector(
          "[data-calculator-global-measurement-toggle]"
        )?.checked
          ? "ft_in"
          : "cm",
        measure_source: "calculator",
      });
    }, DEBOUNCE_MS);
    return;
  }

  /* --- PDP: cm / ft-in toggle -------------------------------------------- */
  if (t.closest("[data-calculator-global-measurement-toggle]")) {
    return emit("pdp_measure_toggle", {
      measure_unit: t.checked ? "ft_in" : "cm",
    });
  }

  /* --- PDP: quantity typed rather than stepped --------------------------- */
  /* The + / − buttons update the input and dispatch a synthetic change, so  */
  /* without the isTrusted guard every step fires twice — once from the      */
  /* click branch, once from here. A real keyboard edit is trusted; the      */
  /* synthetic one isn't.                                                    */
  const qtyInput = t.closest('quantity-selector-component input[name="quantity"]');
  if (qtyInput) {
    if (!e.isTrusted) return;
    return emit("pdp_quantity_change", {
      direction: "typed",
      quantity_to: num(qtyInput.value),
    });
  }

  /* --- PDP: length / width as variant dropdowns ------------------------- */
  /* Stock-length products (most hall runners) pick length and width from  */
  /* variant selects rather than the calculator. Same event name so the     */
  /* "what sizes do people choose" query covers both product types;         */
  /* measure_source says which UI it came from.                             */
  const vsel = t.closest("variant-selector-component select[data-variant-option]");
  if (vsel) {
    const label = document.querySelector(`label[for="${vsel.id}"]`)?.textContent?.trim() || "";
    const key = /length/i.test(label) ? "length"
              : /width/i.test(label)  ? "width"
              : label.toLowerCase().replace(/\s+/g, "_") || `option_${vsel.dataset.index}`;
    const v = vsel.value || "";
    return emit("pdp_measure_input", {
      measure_key: str(key),
      measure_value: str(v),
      measure_unit: /cm/i.test(v) ? "cm" : /ft|in|['"]/.test(v) ? "ft_in" : "unknown",
      measure_source: "variant",
    });
  }

  /* --- PLP: checkbox filters --------------------------------------------- */
  const filter = t.closest('input[name^="filter."]');
  if (filter) {
    return emit("plp_filter_click", {
      filter_group: str(filter.name),
      filter_value: str(filter.value),
      filter_label: str(filter.dataset.chipLabel),
      filter_action: filter.checked ? "apply" : "remove",
      option_count: num(filter.dataset.filterCount),
    });
  }

  /* --- PLP: sort --------------------------------------------------------- */
  const sort = t.closest('select[name="sort_by"]');
  if (sort) {
    return emit("plp_sort_change", { sort_value: str(sort.value) });
  }
});

/* -------------------------------------------------------------------------- */
/* Native <details> accordions                                                 */
/*                                                                             */
/* The PDP accordions are native <details>/<summary>, and `toggle` does not    */
/* bubble — capture phase is the only way to catch it from the document.       */
/* Needs the data-rr-accordion patch in snippets/accordion.liquid.             */
/* -------------------------------------------------------------------------- */

document.addEventListener(
  "toggle",
  (e) => {
    const details = e.target;
    if (!details?.matches?.("details[data-rr-accordion]")) return;
    if (!details.open) return; // only care about opens
    emit("pdp_accordion_open", {
      accordion_label: str(
        details.dataset.accordionLabel ||
          details.querySelector("summary")?.textContent?.trim()
      ),
    });
  },
  { capture: true }
);

/* -------------------------------------------------------------------------- */
/* Theme bus                                                                   */
/*                                                                             */
/* These fire on outcomes rather than clicks, which is what makes them worth   */
/* having: sample:added only fires if the sample actually went in the cart.    */
/* -------------------------------------------------------------------------- */

ThemeBus.addEventListener(EVENTS.sampleAdded, (e) =>
  emit("pdp_sample_add", { sample_variant_id: str(e.detail?.variantId) })
);

ThemeBus.addEventListener(EVENTS.sampleRemoved, (e) =>
  emit("pdp_sample_remove", { sample_variant_id: str(e.detail?.variantId) })
);

ThemeBus.addEventListener(EVENTS.sampleLimitReached, () =>
  emit("pdp_sample_limit_hit", {
    max_samples: num(
      document.querySelector("[data-sample-button]")?.dataset.maxSamples
    ),
  })
);

ThemeBus.addEventListener(EVENTS.calculatorDrawerOpen, () =>
  emit("pdp_calculator_open", {})
);

ThemeBus.addEventListener(EVENTS.cartItemAdded, () =>
  emit("pdp_add_to_cart_success", {})
);

ThemeBus.addEventListener(EVENTS.filtersApplied, (e) => {
  if (e.detail?.status !== "done") return; // ignore the optimistic pass
  const params = str(e.detail.params);
  // The count element re-renders just after this fires; read it next tick.
  setTimeout(() => {
    emit("plp_filter_applied", {
      filter_params: params,
      result_count: num(
        document.querySelector("[data-shown-products-count]")?.textContent?.replace(/\D/g, "")
      ),
    });
  }, 0);
});

ThemeBus.addEventListener(EVENTS.filtersNoResults, (e) =>
  emit("plp_filter_no_results", { filter_params: str(e.detail?.params) })
);